import "./SourcePicker.scss";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Dropdown, DropdownToggleType } from "../../Components";

export enum SourceType {
  PLATFORM = "Platform",
  SOURCE_OF_TRUTH = "Source of Truth",
}

const SOURCE_TYPES = ["Platform", "Source of Truth"];

interface SourcePickerProps {
  applyOnClick: (source) => void;
  label?: string;
  platforms: string[];
  selectedSourceType: `${SourceType}`;
  selectedSourceValue: Record<string, boolean>; // Specific SoT values (e.g. "ga4", "tvad", "shopify", etc.)
  sourceValueOptions: { label: string; value: string }[];
  disabled?: boolean;
  singleSelect?: boolean;
}

/*
 * If platform is the selected source type, there won't be a selected value because you don't select a specific platform.
 * If Source of Truth is selected, you will selected a specific value if you're on the Overview tab, but no selected value when on the other tabs.
 * When in a state where there isn't a selected value, we use the menuRenderOverride to display a string list of options instead of a picker.
 */
const SourcePicker: React.FC<SourcePickerProps> = ({
  applyOnClick,
  label = "Source: ",
  platforms,
  selectedSourceType: selectedSourceTypeInput,
  selectedSourceValue: selectedSourceValueInput,
  sourceValueOptions,
  disabled,
  singleSelect = false,
}) => {
  const [selectedSourceType, setSelectedSourceType] = useState<string | undefined>(
    selectedSourceTypeInput
  );
  const [selectedSourceValues, setSelectedSourceValues] = useState<Record<string, boolean>>(
    selectedSourceValueInput
  );

  useEffect(() => {
    setSelectedSourceType(selectedSourceTypeInput);
    setSelectedSourceValues(selectedSourceValueInput);
  }, [selectedSourceTypeInput, selectedSourceValueInput]);

  const handleSetSourceValue = useCallback(
    (value: string) => {
      if (singleSelect) {
        setSelectedSourceValues({ [value]: !selectedSourceValues[value] });
        return;
      }

      if (selectedSourceValues[value]) {
        setSelectedSourceValues({
          ...selectedSourceValues,
          [value]: !selectedSourceValues[value],
        });
      } else {
        setSelectedSourceValues({
          ...selectedSourceValues,
          [value]: true,
        });
      }
    },
    [selectedSourceValues, singleSelect]
  );

  const sourceTypeOptions = useMemo(() => {
    let options: { label: string; value: string; selected: boolean; className: string }[] = [];
    for (let sourceType of SOURCE_TYPES) {
      // If the only source value is platform, disable the Source of Truth picker.
      const isDisabled =
        sourceType === SourceType.SOURCE_OF_TRUTH &&
        sourceValueOptions.length === 1 &&
        sourceValueOptions[0].value === "platform";

      options.push({
        label: sourceType,
        value: sourceType,
        selected: selectedSourceType === sourceType,
        className: isDisabled ? "disabled" : "",
      });
    }
    return options;
  }, [selectedSourceType, sourceValueOptions]);

  const rightMenuOptions = useMemo(() => {
    let options: { label: string; value: string; selected: boolean; section?: string }[] = [];
    if (selectedSourceType === SourceType.PLATFORM) {
      for (let platform of platforms) {
        options.push({
          label: platform,
          value: platform,
          selected: selectedSourceValues[platform],
        });
      }
    } else {
      for (let sourceOption of sourceValueOptions) {
        if (sourceOption.value !== "platform") {
          options.push({
            ...sourceOption,
            label: sourceOption.label,
            selected: selectedSourceValues[sourceOption.value],
            section: ["tvad", "skai"].includes(sourceOption.value) ? "bottom" : "top",
          });
        }
      }
    }

    return options;
  }, [platforms, selectedSourceType, selectedSourceValues, sourceValueOptions]);

  // Whether to render the menu as a string list instead of a picker
  const menuRenderOverride = useMemo(() => {
    if (selectedSourceType === SourceType.PLATFORM) {
      // 1 is the index of the menu (the second menu in the dropdown)
      return { "1": "stringList" as const };
    }
  }, [selectedSourceType]);

  const multiMenuSections = {
    1: [
      { label: "top", displayLabel: false },
      { label: "bottom", displayLabel: false },
    ],
  };

  const menuSectionTitles = useMemo(() => {
    const titles = {};
    if (selectedSourceType === SourceType.SOURCE_OF_TRUTH && !singleSelect) {
      titles[1] = (
        <div style={{ color: "#DB7712", fontSize: "12px", padding: "7px 24px" }}>
          Selecting multiple sources can lead to over counting.
        </div>
      );
    }

    return titles;
  }, [selectedSourceType, singleSelect]);

  /*
    The label for the dropdown.
    If single select, show the selected Source of Truth value (e.g. "Source: GA4"). For multi-select, just display "Source: Source of Truth".
    For platform, always say "Source: Platform".
  */
  const labelValue = useMemo(() => {
    if (singleSelect) {
      if (selectedSourceType === SourceType.PLATFORM) {
        return `Source: ${selectedSourceType}`;
      } else {
        const value = Object.keys(selectedSourceValues)[0];
        const formattedValue = sourceValueOptions.find(option => option.value === value)?.label;
        return `Source: ${formattedValue}`;
      }
    } else {
      return `Source: ${selectedSourceType}`;
    }
  }, [selectedSourceType, selectedSourceValues, singleSelect, sourceValueOptions]);

  return (
    <div className="crossChannelSourcePicker">
      <Dropdown
        type={DropdownToggleType.OUTLINED}
        value={labelValue}
        options={[]} // This is a placeholder. The actual options are passed in as menuItems
        menuItems={[sourceTypeOptions, rightMenuOptions]}
        multiMenuSections={multiMenuSections}
        menuRenderOverride={menuRenderOverride}
        menuSectionTitles={menuSectionTitles}
        applyOnClick={([selectedSourceTypeArray, selectedSourceValueArray]) => {
          const selectedSourceType = (selectedSourceTypeArray[0] || {}).value;
          const selectedSourceValues = selectedSourceValueArray.map(item => item.value);

          if (selectedSourceType === SourceType.PLATFORM) {
            applyOnClick(["platform"]);
          } else {
            applyOnClick(selectedSourceValues);
          }
        }}
        cancelOnClick={() => {}}
        onChange={(value: string) => {
          // Depending on if youre selecting from left or right menu, the value
          // is either "Platform", "Source of Truth", or the specific SoT value
          if (SOURCE_TYPES.includes(value)) {
            setSelectedSourceType(value);
          } else {
            handleSetSourceValue(value);
          }
        }}
        disabled={disabled}
      />
    </div>
  );
};

export default SourcePicker;
